<script setup>
import { ref, computed } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { MESSAGE_MAX_LENGTH } from 'helpers/MessageTypeHelper';
import { getInboxMaxLength } from 'dashboard/composables/useInbox';
import { REPLY_EDITOR_MODES } from 'components/widgets/WootWriter/constants';

const props = defineProps({
  selectedInboxes: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'send']);

const isSilentMessage = ref(true);
const replyType = ref(REPLY_EDITOR_MODES.REPLY);
const broadcastTitle = ref('');
const messageContent = ref('');
const confirmMode = ref(false);

const isPrivate = computed(() => replyType.value === REPLY_EDITOR_MODES.NOTE);

const getSafeContentLength = computed(() => {
  if (!props.selectedInboxes.length) {
    return MESSAGE_MAX_LENGTH.GENERAL;
  }
  const lengths = props.selectedInboxes.map(inbox => getInboxMaxLength(inbox));
  return Math.min(...lengths);
});

const onClose = () => {
  emit('close');
};

const initiateSend = () => {
  if (messageContent.value.trim()) {
    confirmMode.value = true;
  }
};

// 2. Отмена подтверждения
const cancelSend = () => {
  confirmMode.value = false;
};

// 3. Финальная отправка
const onConfirmSend = () => {
  emit('send', {
    title: broadcastTitle.value.trim(),
    message: messageContent.value.trim(),
    isPrivate: isPrivate.value,
    isSilent: isSilentMessage.value,
  });
  messageContent.value = '';
  confirmMode.value = false;
  onClose();
};

const setReplyMode = mode => {
  replyType.value = mode;
  // Сбрасываем подтверждение при смене типа, чтобы избежать ошибок
  confirmMode.value = false;
};
</script>

<template>
  <div class="modal-backdrop">
    <div class="send-messages-modal">
      <div class="flex items-center justify-between header">
        <span>{{ $t('BULK_ACTION.MESSAGES.TITLE') }}</span>
        <NextButton ghost xs slate icon="i-lucide-x" @click="onClose" />
      </div>

      <div class="send-messages-content">
        <Input
          v-model="broadcastTitle"
          :label="$t('BULK_ACTION.MESSAGES.BROADCAST.TITLE')"
          :placeholder="$t('BULK_ACTION.MESSAGES.BROADCAST.PLACEHOLDER')"
        />

        <div class="woot-writer-root" :class="{ 'is-private': isPrivate }">
          <div class="reply-type-tabs mb-2">
            <button
              class="tab-button"
              :class="{ active: !isPrivate }"
              @click="setReplyMode(REPLY_EDITOR_MODES.REPLY)"
            >
              {{ $t('CONVERSATION.REPLYBOX.REPLY') }}
            </button>
            <button
              class="tab-button"
              :class="{ active: isPrivate }"
              @click="setReplyMode(REPLY_EDITOR_MODES.NOTE)"
            >
              <span v-if="isPrivate" class="icon i-lucide-lock w-3 h-3 mr-1" />
              {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
            </button>
          </div>

          <div class="editor-wrap">
            <Editor
              v-model="messageContent"
              class="large-editor"
              :is-private="isPrivate"
              :max-length="getSafeContentLength"
              :placeholder="
                isPrivate
                  ? $t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT')
                  : $t('BULK_ACTION.MESSAGES.PLACEHOLDER')
              "
            />
          </div>
        </div>

        <div class="send-messages-footer">
          <transition name="fade" mode="out-in">
            <div
              v-if="!confirmMode"
              key="normal"
              class="flex items-center gap-4"
            >
              <div class="flex items-center gap-2 flex-1">
                <div class="flex items-center gap-1">
                  <div class="group relative flex items-center justify-center">
                    <span
                      class="icon i-lucide-info w-4 h-4 text-slate-500 cursor-help transition-colors group-hover:text-slate-700 dark:group-hover:text-slate-300"
                    />
                    <div
                      class="pointer-events-none absolute bottom-full left-1/2 mb-2 w-max max-w-[240px] -translate-x-1/2 opacity-0 transform scale-95 transition-all duration-200 ease-out group-hover:pointer-events-auto group-hover:opacity-100 group-hover:scale-100 z-50"
                    >
                      <div
                        class="relative rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white shadow-xl dark:bg-slate-100 dark:text-slate-900 text-center leading-relaxed"
                      >
                        {{ $t('BULK_ACTION.MESSAGES.SILENT_DESCRIPTION') }}
                        <div
                          class="absolute -bottom-1 left-1/2 h-2.5 w-2.5 -translate-x-1/2 rotate-45 bg-slate-900 dark:bg-slate-100"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <Switch v-model="isSilentMessage" />
                <span class="text-sm text-slate-600 dark:text-slate-400">
                  {{ $t('BULK_ACTION.MESSAGES.SILENT_MESSAGE') }}
                </span>
              </div>

              <NextButton
                class="w-1/2 justify-center"
                size="md"
                :label="
                  isPrivate
                    ? $t('CONVERSATION.REPLYBOX.CREATE')
                    : $t('BULK_ACTION.MESSAGES.SEND')
                "
                :color-scheme="isPrivate ? 'warning' : 'primary'"
                :disabled="!messageContent.trim()"
                @click="initiateSend"
              />
            </div>

            <div
              v-else
              key="confirm"
              class="flex items-center justify-between bg-n-alpha-1 rounded-lg p-1 animate-in fade-in slide-in-from-bottom-2 duration-200"
            >
              <span
                class="text-sm font-medium text-slate-700 dark:text-slate-200 px-3"
              >
                {{
                  isPrivate
                    ? $t('BULK_ACTION.MESSAGES.CONFIRMATION.PRIVATE_NOTE')
                    : $t('BULK_ACTION.MESSAGES.CONFIRMATION.SEND_MESSAGE')
                }}
              </span>
              <div class="flex gap-2">
                <NextButton
                  size="sm"
                  ghost
                  :label="$t('BULK_ACTION.MESSAGES.CONFIRMATION.CANCEL')"
                  @click="cancelSend"
                />
                <NextButton
                  size="sm"
                  :color-scheme="isPrivate ? 'warning' : 'alert'"
                  :icon="isPrivate ? 'i-lucide-check' : 'i-lucide-send'"
                  :label="
                    isPrivate
                      ? $t('BULK_ACTION.MESSAGES.CONFIRMATION.CONFIRM_PRIVATE')
                      : $t('BULK_ACTION.MESSAGES.CONFIRMATION.CONFIRM_SEND')
                  "
                  @click="onConfirmSend"
                />
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.modal-backdrop {
  @apply fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4;
}

.send-messages-modal {
  @apply w-full max-w-2xl mx-auto bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-n-weak flex flex-col;

  .header {
    @apply p-4 border-b border-n-weak;
    span {
      @apply text-lg font-semibold text-slate-800 dark:text-slate-100;
    }
  }
}

.send-messages-content {
  @apply flex flex-col p-4 gap-4;

  .woot-writer-root {
    @apply relative border border-n-weak rounded-xl bg-n-solid-1 transition-colors duration-300 ease-in-out;

    .reply-type-tabs {
      @apply flex items-center gap-1 p-2 pb-0;

      .tab-button {
        @apply flex items-center px-3 py-1.5 text-sm font-medium rounded-t-lg transition-colors text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12;

        &.active {
          @apply bg-n-alpha-2 text-n-slate-12;
        }
      }
    }

    .editor-wrap {
      @apply px-2 pb-2;

      .large-editor {
        @apply border-none shadow-none bg-transparent focus-within:ring-0 focus-within:border-transparent p-0;

        ::v-deep .ProseMirror {
          min-height: 150px;
          max-height: 50vh;
          overflow-y: auto;
          @apply p-3 text-base leading-relaxed w-full outline-none;
        }
      }
    }

    &.is-private {
      @apply bg-n-solid-amber dark:border-n-amber-3/10 border-n-amber-12/5;

      .reply-type-tabs {
        .tab-button.active {
          @apply text-n-amber-12 bg-n-amber-4;
        }
      }
    }
  }

  .send-messages-footer {
    // Фиксируем высоту футера, чтобы он не прыгал при смене контента (опционально, но красиво)
    @apply min-h-[44px] relative z-0 w-full;
  }
}

// Vue Transition Styles
.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}
</style>
